import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useUser } from "../../Context/UserContext";
import { DataGrid, GridToolbar, useGridApiRef } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Link, useParams, useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

//  Utility function to calculate required width for a single line
const calculateColumnWidth = (headerName, minWidth = 120) => {
  const calculatedWidth = Math.ceil(headerName.length * 8.5) + 50;
  return Math.max(minWidth, calculatedWidth);
};

//  Color definitions matching your MUI theme
const COLORS = {
  centerGroup: "FFD9EAD3", // Green background for Center Info
  summaryGroup: "FFD9EAD3", // Green background for Summary
  saplingsGroup: "FFF1C27D", // Orange background for Saplings
  headerBackground: "FFE8EAF6", // Light purple for column headers
  headerFont: "00000000", // Black font
  whiteFont: "FFFFFFFF", // White font
  border: "FFBDBDBD", // Light gray border
};

const ProductionCenterReport = () => {
  const [reportData, setReportData] = useState([]);
  const [allSpecies, setAllSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false); //  Added exporting state
  const { token } = useUser();
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;

    setLoading(true);
    const params = {};
    if (id) params.block_id = id;

    axios
      .get("/api/center/productioncenter-summary", {
        headers: { Authorization: `Bearer ${token}` },
        params: params,
      })
      .then((res) => {
        if (res.data && res.data.centers) {
          setAllSpecies(res.data.allSpecies || []);

          const processedData = res.data.centers.map((center, index) => {
            return {
              id: center.center_id || index,
              sno: index + 1,
              center_name: center.center_name,
              total_target: center.total_target || 0,
              total_stock_saplings: center.total_stock_saplings || 0,
              species_count: center.species_count || 0,
              saplingLookup: center.saplingLookup || {},
              total_selled: center.total_selled || 0,
              total_selled_price: center.total_selled_price || 0,
            };
          });

          setReportData(processedData);
        }
      })
      .catch((err) => console.error("Error fetching center data:", err))
      .finally(() => setLoading(false));
  }, [token, id]);

  const { columns, columnGroupingModel } = useMemo(() => {
    const baseColumns = [
      { field: "sno", headerName: "S.No", width: 70, pinning: "left" },
      {
        field: "center_name",
        headerName: "Production Center",
        width: 250,
        pinning: "left",
        // renderCell: (params) => (
        //   <Link
        //     to={`/production-center/${params.row.id}/details`}
        //     style={{
        //       textDecoration: "none",
        //       color: "#1976d2",
        //       fontWeight: "500",
        //     }}
        //   >
        //     {params.value}
        //   </Link>
        // ),
      },
    ];

    const summaryColumns = [
      {
        field: "species_count",
        headerName: "Species Count",
        width: 150,
        pinning: "right",
      },
      {
        field: "total_target",
        headerName: "Target",
        width: 120,
        pinning: "right",
      },
      {
        field: "total_stock_saplings",
        headerName: "Stock",
        width: 120,
        pinning: "right",
      },
      {
        field: "total_selled",
        headerName: "Total Sold",
        width: 140,
        pinning: "right",
      },
      {
        field: "total_selled_price",
        headerName: "Total Sold Price",
        width: 170,
        pinning: "right",
      },
    ];

    const speciesColumns = allSpecies.map((s) => {
      const name = s.species_name || "";
      return {
        field: `species_${s.id}`,
        headerName: name,
        width: calculateColumnWidth(name, 120),
        type: "number",
        valueGetter: (value, row) => {
          return row?.saplingLookup?.[String(s.id)]?.qty || 0;
        },
      };
    });

    const groups = [
      {
        groupId: "Summary",
        description: "Summary Statistics",
        children: [
          { field: "species_count" },
          { field: "total_target" },
          { field: "total_stock_saplings" },
          { field: "total_selled" },
          { field: "total_selled_price" },
        ],
        headerClassName: "summary-group-header",
      },
      {
        groupId: "Saplings",
        description: "List of All Sapling Species",
        children: allSpecies.map((s) => ({ field: `species_${s.id}` })),
        headerClassName: "saplings-group-header",
      },
    ];

    return {
      columns: [...baseColumns, ...summaryColumns, ...speciesColumns],
      columnGroupingModel: groups,
    };
  }, [allSpecies, reportData]);

  //  Excel Export Function with Styling
  const exportToExcel = useCallback(async () => {
    setExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Production Center Report", {
        properties: { tabColor: { argb: "FF1976D2" } },
      });

      // Define columns for export
      const baseColumns = [
        { header: "S.No", field: "sno" },
        { header: "Production Center", field: "center_name" },
      ];

      const summaryColumns = [
        { header: "Species Count", field: "species_count" },
        { header: "Target", field: "total_target" },
        { header: "Stock", field: "total_stock_saplings" },
        { header: "Total Sold", field: "total_selled" },
        { header: "Total Sold Price", field: "total_selled_price" },
      ];

      const speciesColumns = allSpecies.map((s) => ({
        header: s.species_name || "",
        field: `species_${s.id}`,
        getValue: (row) => row?.saplingLookup?.[String(s.id)]?.qty || 0,
      }));

      const allColumns = [...baseColumns, ...summaryColumns, ...speciesColumns];

      // Calculate column positions
      const baseColCount = baseColumns.length; // 2
      const summaryColCount = summaryColumns.length; // 5
      const speciesColCount = speciesColumns.length;

      // Helper to apply borders
      const applyBorder = (cell, sides = {}) => {
        cell.border = {
          top: sides.top || { style: "thin", color: { argb: COLORS.border } },
          bottom: sides.bottom || {
            style: "thin",
            color: { argb: COLORS.border },
          },
          left: sides.left || { style: "thin", color: { argb: COLORS.border } },
          right: sides.right || {
            style: "thin",
            color: { argb: COLORS.border },
          },
        };
      };

      // ============================================
      // ROW 1: Group Headers (Merged Cells)
      // ============================================

      // Center Info Group (columns 1-2)
      worksheet.mergeCells(1, 1, 1, baseColCount);
      const centerGroupCell = worksheet.getCell(1, 1);
      centerGroupCell.value = "Center Info";
      centerGroupCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.centerGroup },
      };
      centerGroupCell.font = {
        bold: true,
        size: 12,
        color: { argb: COLORS.headerFont },
      };
      centerGroupCell.alignment = { horizontal: "center", vertical: "middle" };
      applyBorder(centerGroupCell);
      for (let col = 2; col <= baseColCount; col++)
        applyBorder(worksheet.getCell(1, col));

      // Summary Group (columns 3-7)
      const summaryStart = baseColCount + 1;
      const summaryEnd = baseColCount + summaryColCount;
      worksheet.mergeCells(1, summaryStart, 1, summaryEnd);
      const summaryGroupCell = worksheet.getCell(1, summaryStart);
      summaryGroupCell.value = "Summary";
      summaryGroupCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: COLORS.summaryGroup },
      };
      summaryGroupCell.font = {
        bold: true,
        size: 12,
        color: { argb: COLORS.headerFont },
      };
      summaryGroupCell.alignment = { horizontal: "center", vertical: "middle" };
      applyBorder(summaryGroupCell);
      for (let col = summaryStart + 1; col <= summaryEnd; col++)
        applyBorder(worksheet.getCell(1, col));

      // Saplings Group (remaining columns)
      const saplingsStart = summaryEnd + 1;
      const saplingsEnd = summaryEnd + speciesColCount;
      if (speciesColCount > 0) {
        worksheet.mergeCells(1, saplingsStart, 1, saplingsEnd);
        const saplingsGroupCell = worksheet.getCell(1, saplingsStart);
        saplingsGroupCell.value = "Saplings";
        saplingsGroupCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: COLORS.saplingsGroup },
        };
        saplingsGroupCell.font = {
          bold: true,
          size: 12,
          color: { argb: COLORS.headerFont },
        };
        saplingsGroupCell.alignment = {
          horizontal: "center",
          vertical: "middle",
        };
        applyBorder(saplingsGroupCell);
        for (let col = saplingsStart + 1; col <= saplingsEnd; col++)
          applyBorder(worksheet.getCell(1, col));
      }

      // ============================================
      // ROW 2: Column Headers
      // ============================================
      const headerRow = worksheet.addRow(allColumns.map((c) => c.header));
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: COLORS.headerBackground },
        };
        cell.font = {
          bold: true,
          size: 10,
          color: { argb: COLORS.headerFont },
        };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: false,
        };
        applyBorder(cell, {
          bottom: { style: "medium", color: { argb: "FF666666" } },
        });
      });

      // ============================================
      // DATA ROWS
      // ============================================
      reportData.forEach((row, rowIndex) => {
        const rowData = allColumns.map((col) => {
          if (col.getValue) return col.getValue(row);
          return row[col.field];
        });
        const dataRow = worksheet.addRow(rowData);

        const isEven = rowIndex % 2 === 0;
        const rowFill = isEven
          ? { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } }
          : {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF5F5F5" },
            };

        dataRow.eachCell((cell, colNumber) => {
          cell.fill = rowFill;
          applyBorder(cell);

          if (typeof cell.value === "number") {
            cell.alignment = { horizontal: "right", vertical: "middle" };
            // Apply currency format specifically to Total Sold Price
            if (colNumber === summaryEnd) {
              cell.numFmt = "#,##0.00";
            } else {
              cell.numFmt = "#,##0";
            }
          } else {
            cell.alignment = { horizontal: "left", vertical: "middle" };
          }
        });
      });

      // ============================================
      // COLUMN WIDTHS & FEATURES
      // ============================================
      worksheet.columns.forEach((column, index) => {
        const header = allColumns[index].header;
        let maxWidth = header.length + 4;
        const sampleRows = reportData.slice(0, 10);
        sampleRows.forEach((row) => {
          const col = allColumns[index];
          const value = col.getValue ? col.getValue(row) : row[col.field];
          if (value !== null && value !== undefined) {
            maxWidth = Math.max(maxWidth, String(value).length + 2);
          }
        });
        column.width = Math.min(maxWidth * 1.2, 30);
      });

      worksheet.views = [
        { state: "frozen", xSplit: 2, ySplit: 2, topLeftCell: "C3" },
      ];
      worksheet.autoFilter = {
        from: { row: 2, column: 1 },
        to: { row: 2, column: allColumns.length },
      };

      // ============================================
      // DOWNLOAD
      // ============================================
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(
        blob,
        `Production_Center_Report_Block_${id || "All"}_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (error) {
      console.error("Excel export error:", error);
      alert("Failed to export Excel file. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [reportData, allSpecies, id]);

  return (
    <Box
      sx={{
        height: "calc(100vh - 60px)",
        display: "flex",
        flexDirection: "column",
        p: 2,
        backgroundColor: "#f4f6f8",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 1,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by Center Name..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: "300px", backgroundColor: "white", borderRadius: "4px" }}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          {/* ✅ Updated Export Button */}
          <Button
            variant="contained"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            disabled={exporting || loading}
            sx={{ minWidth: 180, gap: 1 }}
          >
            {exporting ? "Exporting..." : "Download Excel"}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          bgcolor: "white",
          borderRadius: 1,
          boxShadow: 3,
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <DataGrid
          apiRef={apiRef}
          rows={reportData}
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          loading={loading}
          density="compact"
          rowHeight={38}
          autoHeight={false}
          disableRowSelectionOnClick
          filterModel={{
            items: [
              {
                id: 1,
                field: "center_name",
                operator: "contains",
                value: searchText,
              },
            ],
          }}
          sx={{
            border: "none",
            height: "100%",
            width: "100%",
            ".MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              whiteSpace: "nowrap",
            },
            "& .saplings-group-header .MuiDataGrid-columnHeaderTitleContainer":
              {
                backgroundColor: "#f1c27d",
                justifyContent: "center",
              },
            "& .summary-group-header .MuiDataGrid-columnHeaderTitleContainer": {
              backgroundColor: "#d9ead3",
              justifyContent: "center",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflowX: "auto !important",
            },
          }}
          slots={{ toolbar: GridToolbar }}
          pinnedColumns={{
            left: ["sno", "center_name"],
            right: [
              "species_count",
              "total_target",
              "total_stock_saplings",
              "total_selled",
              "total_selled_price",
            ],
          }}
        />
      </Box>
    </Box>
  );
};

export default ProductionCenterReport;
